import * as React from 'react';

import './emojiPicker.scss';

import { useTypedTranslation } from 'shared/entities/localization';
import { useResizeLayoutNotify } from 'shared/entities/common/hooks';

import { type EmojiMartSelected } from './types';

export type { EmojiMartSelected } from './types';

const EMOJI_BUTTON_HOVER_BG = '#f2f3fd';
export const EMOJI_POPUP_MAX_WIDTH = 316;

const LazyPicker = React.lazy(async () => {
  const [{ default: Picker }, { default: data }] = await Promise.all([
    import(/* webpackChunkName: "vendor-emoji-picker" */ '@emoji-mart/react'),
    import(
      /* webpackChunkName: "vendor-emoji-picker-data" */ '@emoji-mart/data'
    )
  ]);

  const EmbeddedPicker = (props: Record<string, unknown>) => {
    const pickerProps = { ...props };
    delete pickerProps.data;
    return <Picker data={data} {...pickerProps} />;
  };

  return { default: EmbeddedPicker };
});

/** Локаль пикера: только `ru` и `en` (как в UI приложения). */
function emojiPickerLocaleFromAppLanguage(language: string): 'en' | 'ru' {
  const base = language.toLowerCase().split(/[-_]/)[0];
  return base === 'ru' ? 'ru' : 'en';
}

export type LazyEmojiPickerProps = {
  onReady?: () => void;
  onEmojiSelect?: (emoji: EmojiMartSelected) => void;
  locale?: string;
  dynamicWidth?: boolean;
  emojiSize?: number;
  emojiButtonSize?: number;
  perLine?: number;
};

const PickerWithCallback: React.FC<LazyEmojiPickerProps> = ({
  onReady,
  onEmojiSelect,
  dynamicWidth = false,
  locale: localeProp,
  emojiSize = 20,
  emojiButtonSize = 32,
  perLine = 9
}) => {
  const { lng } = useTypedTranslation();
  const locale = localeProp ?? emojiPickerLocaleFromAppLanguage(lng);
  const measureRootRef = React.useRef<HTMLDivElement>(null);

  useResizeLayoutNotify(measureRootRef, onReady);

  const handleSelect = React.useCallback(
    (emoji: unknown) => {
      const e = emoji as EmojiMartSelected;
      if (
        (e?.native == null || e.native.length === 0) &&
        (e?.unified == null || e.unified.length === 0)
      ) {
        return;
      }
      onEmojiSelect?.(e);
    },
    [onEmojiSelect]
  );

  const picker = (
    <LazyPicker
      dynamicWidth={dynamicWidth}
      set="native"
      locale={locale}
      theme="light"
      previewPosition="none"
      searchPosition="sticky"
      skinTonePosition="preview"
      emojiSize={emojiSize}
      emojiButtonSize={emojiButtonSize}
      emojiButtonColors={[EMOJI_BUTTON_HOVER_BG]}
      perLine={perLine}
      onEmojiSelect={handleSelect}
    />
  );

  if (!onReady) {
    return picker;
  }

  return (
    <div
      ref={measureRootRef}
      style={{
        width: '100%',
        minWidth: 0,
        minHeight: 1
      }}
    >
      {picker}
    </div>
  );
};

const LazyEmojiPicker: React.FC<LazyEmojiPickerProps> = (props) => (
  <React.Suspense fallback={null}>
    <PickerWithCallback {...props} />
  </React.Suspense>
);

export default React.memo(LazyEmojiPicker);
